import { getAdminToken } from "@/utils/adminAuth";
import type { ApiResponse } from "@/types/api";
import type {
  ModerationStats,
  ModerationQueueItem,
  AdminExperience,
  CompanySuggestion,
  AdminCompany,
} from "@/types/admin";

async function adminRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await getAdminToken();
  if (!token) throw new Error("Not authenticated");

  const res = await fetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error?.message ?? `Request failed: ${res.status}`);
  }

  const json: ApiResponse<T> = await res.json();
  return json.data;
}

// Moderation
export function getModerationStats(): Promise<ModerationStats> {
  return adminRequest<ModerationStats>("/api/admin/moderation/stats");
}

export function getModerationQueue(limit = 20): Promise<ModerationQueueItem[]> {
  return adminRequest<ModerationQueueItem[]>(
    `/api/admin/moderation/queue?limit=${limit}`,
  );
}

export function getModerationItem(experienceId: string): Promise<{
  queue: ModerationQueueItem;
  experience: AdminExperience | null;
}> {
  return adminRequest(`/api/admin/moderation/${experienceId}`);
}

export function approveExperience(
  experienceId: string,
  note?: string,
): Promise<{ id: string; status: string }> {
  return adminRequest(`/api/admin/moderation/${experienceId}/approve`, {
    method: "PUT",
    body: JSON.stringify({ note: note || "Approved by moderator" }),
  });
}

export function rejectExperience(
  experienceId: string,
  reason: string,
  note?: string,
): Promise<{ id: string; status: string }> {
  return adminRequest(`/api/admin/moderation/${experienceId}/reject`, {
    method: "PUT",
    body: JSON.stringify({ reason, note }),
  });
}

export function editExperience(
  experienceId: string,
  sanitizedContext: string,
  note?: string,
): Promise<{ id: string; status: string; decision: string }> {
  return adminRequest(`/api/admin/moderation/${experienceId}/edit`, {
    method: "PUT",
    body: JSON.stringify({ sanitizedContext, note }),
  });
}

// Experiences
export function getExperiences(
  status?: string,
  limit = 50,
): Promise<AdminExperience[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status) params.set("status", status);
  return adminRequest<AdminExperience[]>(`/api/admin/experiences?${params}`);
}

// Company Suggestions
export function getCompanySuggestions(): Promise<CompanySuggestion[]> {
  return adminRequest<CompanySuggestion[]>("/api/admin/company-suggestions");
}

export function approveCompanySuggestion(
  id: string,
  country?: string,
): Promise<{ id: string; companyId: string; status: string }> {
  return adminRequest(`/api/admin/company-suggestions/${id}/approve`, {
    method: "PUT",
    body: JSON.stringify({ country }),
  });
}

export function rejectCompanySuggestion(
  id: string,
  reason?: string,
): Promise<{ id: string; status: string }> {
  return adminRequest(`/api/admin/company-suggestions/${id}/reject`, {
    method: "PUT",
    body: JSON.stringify({ reason }),
  });
}

// Companies
export function getCompanies(limit = 50): Promise<AdminCompany[]> {
  return adminRequest<AdminCompany[]>(
    `/api/admin/company-suggestions/all?limit=${limit}`,
  );
}

export function updateCompany(
  id: string,
  updates: { name?: string; country?: string; status?: string },
): Promise<{ id: string }> {
  return adminRequest(`/api/admin/company-suggestions/all/${id}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
}
